/**
 * BoVio API - Offer check response
 */
export interface OfferCheck {
  /**
   * List of cancellation penalties
   */
  cancellationPenalties: CancellationPenalty[]
  /**
   * The cashback information.
   */
  cashback?: Cashback
  /**
   * The first charge attempt due date.
   */
  chargeDueAt?: null | string
  /**
   * The check-in from the offer.
   */
  checkIn: string
  /**
   * The check-in instructions to the customer
   */
  checkInInstructions?: string
  /**
   * The check-out from the offer.
   */
  checkOut: string
  /**
   * The deposit amount for Charge Later.
   */
  depositAmount?: null | string
  /**
   * Date and time (UTC) when the deal freeze expires.
   */
  expiresAt?: null | string
  /**
   * Extra parameters required to perform some action in the consumer side
   */
  extraParams?: null | ExtraParams
  /**
   * Links generated for the offer
   */
  links: Link[]
  /**
   * Room meals
   */
  meals?: string[]
  /**
   * Name of the entity that processes the customer payment
   */
  merchantOfRecord: MerchantOfRecord
  /**
   * Prices
   */
  prices: Price[]
  /**
   * Provider code
   */
  providerCode?: null | string
  /**
   * Provider trader information
   */
  providerTraderInformation?: null | ProviderTraderInformation
  room?: null | Room
  rooms?: Array<null | Room>
  /**
   * Offer status
   */
  status: Status
}

/**
 * Cancellation penalty
 */
export interface CancellationPenalty {
  /**
   * Fixed amount of the penalty
   */
  amount: string
  /**
   * Currency of the fixed amount
   */
  currency: string
  /**
   * End date and time of the penalty
   */
  end: string
  /**
   * Start date and time of the penalty
   */
  start: string
}

/**
 * The cashback information.
 */
export interface Cashback {
  /**
   * The type of cashback.
   */
  type?: CashbackType
  /**
   * The value of cashback.
   */
  value?: number
}

/**
 * The type of cashback.
 */
export enum CashbackType {
  Points = 'points'
}

export interface ExtraParams {
  /**
   * Final provider code.
   */
  finalProviderCode?: string
  /**
   * Cardinal Commerce Credit Card validation code. Returned only in EPS offers
   */
  jwt?: string
}

/**
 * Link
 */
export interface Link {
  /**
   * URL
   */
  href: string
  /**
   * HTTP method
   */
  method: Method
  /**
   * Type
   */
  type: LinkType
}

/**
 * HTTP method
 */
export enum Method {
  Get = 'GET',
  Post = 'POST'
}

/**
 * Type
 */
export enum LinkType {
  BookingCreation = 'booking_creation',
  BookingCreationAsync = 'booking_creation_async',
  BundleBookingCreation = 'bundle_booking_creation',
  ChargeLaterBookingCreation = 'charge_later_booking_creation',
  PaymentMethods = 'payment_methods'
}

/**
 * Name of the entity that processes the customer payment
 */
export enum MerchantOfRecord {
  Expedia = 'expedia',
  Findhotel = 'findhotel',
  Getaroom = 'getaroom',
  Hotel = 'hotel',
  Priceline = 'priceline'
}

/**
 * Price
 */
export interface Price {
  /**
   * Chargeable information
   */
  chargeable: Chargeable
  /**
   * @deprecated Use chargeable.currency and hotelFees.currency instead.
   */
  currencyCode: string
  /**
   * Hotel fees information
   */
  hotelFees: HotelFees
  /**
   * Amount to be paid at the hotel. Hotel fees for pay now offers. Hotel fees and booking
   * total price for pay later offers. This object is not returned when the total value is not
   * available in the hotel currency.
   */
  payAtHotel?: null | PayAtHotel
  /**
   * Taxes information
   */
  taxes?: null | Taxes
  /**
   * The type of the price
   */
  type: PriceType
}

/**
 * Chargeable information
 */
export interface Chargeable {
  /**
   * Total rate without taxes
   */
  base: string
  /**
   * Currency of the chargeable price
   */
  currency: string
  /**
   * Total of all taxes
   */
  taxes: string
  /**
   * Total of base rate and taxes
   */
  total: string
}

/**
 * Hotel fees information
 */
export interface HotelFees {
  /**
   * The breakdown of the hotel fees per type
   */
  breakdown: HotelFeesBreakdown[]
  /**
   * Currency of the hotel fees
   */
  currency: string
  /**
   * Total of all the hotel fees
   */
  total: string
}

export interface HotelFeesBreakdown {
  /**
   * The elements of the hotel fees that composes the "other" type for more than one remaining
   * fee type.
   */
  elements?: TypeElement[]
  /**
   * The amount charged for the type
   */
  total?: string
  /**
   * The type of the hotel fees
   */
  type?: TypeElement
}

/**
 * The type of the hotel fees
 */
export enum TypeElement {
  CityTax = 'city_tax',
  CleaningFee = 'cleaning_fee',
  DestinationFee = 'destination_fee',
  EnvironmentFee = 'environment_fee',
  GoodsAndServicesTax = 'goods_and_services_tax',
  GovernmentTax = 'government_tax',
  MunicipalityFee = 'municipality_fee',
  Other = 'other',
  ResortFee = 'resort_fee',
  ServiceCharge = 'service_charge',
  Tax = 'tax',
  TourismFee = 'tourism_fee'
}

export interface PayAtHotel {
  /**
   * Hotel currency
   */
  currency: string
  /**
   * Total to be paid at the hotel
   */
  total: string
}

export interface Taxes {
  /**
   * The breakdown of the taxes per type
   */
  breakdown: TaxesBreakdown[]
  /**
   * Currency of the taxes
   */
  currency: string
  /**
   * Total of all the taxes
   */
  total: string
}

export interface TaxesBreakdown {
  /**
   * The amount charged for the type
   */
  total?: string
  /**
   * The type of the taxes
   */
  type?: TaxesType
}

/**
 * The type of the taxes
 */
export enum TaxesType {
  Tax = 'tax',
  Vat = 'vat'
}

/**
 * The type of the price
 */
export enum PriceType {
  ChargeablePrice = 'chargeable_price',
  DisplayPrice = 'display_price',
  HotelPrice = 'hotel_price'
}

export interface ProviderTraderInformation {
  /**
   * Country code.
   */
  countryCode?: null | string
  /**
   * Trader name.
   */
  name?: null | string
  /**
   * Phone number
   */
  phoneNumber?: null | string
  /**
   * The URL of the terms and conditions. It can point to a PDF or a static HTML page.
   */
  termsAndConditionsUrl?: null | string
}

export interface Room {
  /**
   * Room amenities
   */
  amenities?: string[]
  /**
   * Room bedrooms
   */
  bedrooms?: Bedroom[]
  /**
   * The check-in for each room.
   */
  checkIn: string
  /**
   * The check-out for each room.
   */
  checkOut: string
  /**
   * Room description
   */
  description?: null | string
  /**
   * Translated room name to be displayed to the user
   */
  displayName?: string
  /**
   * Room ID
   */
  id?: null | string
  /**
   * Room images
   */
  images?: Image[]
  /**
   * Room master ID
   */
  masterId?: null | string
  /**
   * Room meals
   */
  meals?: string[]
  /**
   * Room occupancy
   */
  occupancy?: null | Occupancy
  /**
   * Provider code
   */
  providerCode?: null | string
}

/**
 * Bedroom information
 */
export interface Bedroom {
  /**
   * Bed configurations
   */
  bedConfigurations?: BedConfiguration[]
  /**
   * Bedroom description
   */
  description?: null | string
  /**
   * Bedroom name
   */
  name?: null | string
}

/**
 * Bed configuration
 */
export interface BedConfiguration {
  /**
   * Number of beds
   */
  count?: number | null
  /**
   * Bed configuration description
   */
  description?: null | string
  /**
   * Bed configuration name
   */
  name?: null | string
  /**
   * Bed size
   */
  size?: null | string
  /**
   * Bed type
   */
  type?: null | string
}

/**
 * Room image
 */
export interface Image {
  /**
   * Image url
   */
  url: string
}

export interface Occupancy {
  /**
   * Maximum number of guests allowed
   */
  maxAllowed?: null | MaxAllowed
}

export interface MaxAllowed {
  /**
   * Maximum number of adults allowed
   */
  adults?: number | null
  /**
   * Maximum number of children allowed
   */
  children?: number | null
  /**
   * Maximum number of extra beds allowed
   */
  extraBeds?: number | null
  /**
   * Maximum total number of guests allowed
   */
  total?: number | null
}

/**
 * Offer status
 */
export enum Status {
  Available = 'available',
  PriceMismatch = 'price_mismatch'
}
